import { useState, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../hooks/useBooking';
import { useSiteColors } from '../../hooks/useSiteColors';
import { formatPrice } from '../../types';
import type { PublicService } from '../../types';

export function ServicesSection() {
  const navigate = useNavigate();
  const { services, setSelectedService } = useBooking();
  const c = useSiteColors();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const handleServiceClick = (service: PublicService) => {
    setSelectedService(service);
    navigate('/book/date-time');
  };

  const categories = useMemo(() => {
    const cats = new Set(services.map((s) => s.category).filter(Boolean) as string[]);
    return Array.from(cats);
  }, [services]);

  const showChips = categories.length > 1;

  const filteredServices = useMemo(() => {
    if (selectedCategory === 'All') return services;
    return services.filter((s) => s.category === selectedCategory);
  }, [services, selectedCategory]);

  const ServiceCard = ({ service }: { service: PublicService }) => (
    <button
      onClick={() => handleServiceClick(service)}
      className="group rounded-2xl p-7 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden w-full"
      style={{ backgroundColor: c.background, border: `1px solid ${c.border}` }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px] transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"
        style={{ backgroundColor: c.primaryFill }}
      />
      {service.image_url && (
        <img src={service.image_url} alt={service.name} loading="lazy" className="w-full h-40 rounded-xl object-cover mb-5" />
      )}
      <h3 className="text-lg font-semibold mb-2" style={{ color: c.text }}>
        {service.name}
      </h3>
      {service.description && (
        <p className="text-sm leading-relaxed mb-5 line-clamp-2" style={{ color: c.mutedText }}>
          {service.description}
        </p>
      )}
      <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${c.border}` }}>
        <span className="text-xl font-bold" style={{ color: c.text }}>
          {formatPrice(service.price_cents, service.currency)}
        </span>
        <span className="text-sm font-medium flex items-center gap-1" style={{ color: c.mutedText }}>
          <Clock className="w-3.5 h-3.5" />
          {service.duration_minutes} min
        </span>
      </div>
    </button>
  );

  return (
    <section id="services" className="py-20">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="mb-12">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase mb-3" style={{ color: c.link }}>
            Our Services
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-3" style={{ color: c.text }}>
            What we offer
          </h2>
          <p className="text-base max-w-lg" style={{ color: c.mutedText }}>
            Select a service to book your appointment.
          </p>
          {showChips && (
            <div className="flex flex-wrap gap-2 mt-5">
              {['All', ...categories].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                  style={
                    selectedCategory === cat
                      ? { backgroundColor: c.primaryFill, color: c.onPrimary, border: `1px solid ${c.primaryOutline ?? c.primaryFill}` }
                      : { backgroundColor: c.background, color: c.mutedText, border: `1px solid ${c.border}` }
                  }
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
        {services.length === 0 && (
          <div className="text-center py-16">
            <p style={{ color: c.mutedText }}>No services available at this time.</p>
          </div>
        )}
      </div>
    </section>
  );
}
